'use strict';
import isObject from 'lodash/isObject';
import 'rxjs/add/observable/of';
import 'rxjs/add/operator/catch';
import 'rxjs/add/operator/filter';
import 'rxjs/add/operator/map';
import 'rxjs/add/operator/mergeMap';
import 'rxjs/add/operator/takeUntil';
import {Observable} from 'rxjs/Observable';
import {AjaxFactory, HttpMethod} from '../../_libs/redux-observable-list';
import {buildInstanceSyncNotifier} from '../../auth/utils/redux-lists';
import {BASE_REDUX_LIST_OPTIONS} from '../../config/redux-list';
import {epicErrorHandler} from '../../core-utilities/rx-utils';
import {CHAT_CHANNEL_COLLECTION} from '../domain/ConversationDomain';


/**
 * @typedef {Object} ChatMessagesModalSupportEpicsOptions
 * @property {ChatModalActions} actions
 * @property {Function} getState
 * @property {AjaxFactory} [getChannelFactory]
 * @property {AjaxFactory} [linkChannelFactory]
 * @property {bunyan} logger
 */

/**
 * @param {ChatMessagesModalSupportEpicsOptions} options
 */
export function buildModalSupportEpics(options) {
  /** @type {ChatModalActions} */
  const actions = options.actions;
  const logger = options.logger;
  const getChannelFactory = options.getChannelFactory || buildGetChannelFactory(BASE_REDUX_LIST_OPTIONS);
  const linkChannelFactory = options.linkChannelFactory || buildLinkChannelFactory(BASE_REDUX_LIST_OPTIONS);

  onShowModalGetOrLinkChannel.$inject = [
    'applicationPermissionsService',
  ];

  return [
    onShowModalGetOrLinkChannel,
    onRequestFinalize,
    getUpdatedChannel,
  ];

  /**
   * @param {ActionsObservable<ReduxAction>} action$
   * @param {Store} store
   * @param dependencies
   * @return {Observable<any>}
   */
  function onShowModalGetOrLinkChannel(action$, store, dependencies) {
    const watchPermissions = buildWatchPermissions(dependencies);

    return action$.ofType(actions.showModal.TYPE)
      .switchMap(showAction => {
        const channel = showAction.payload.channel;
        if (showAction.payload.parentId && isObject(showAction.payload.channel)) {
          return watchChannel(action$, showAction.payload.parentId, store.getState(), channel, dependencies)
            .catch(err => Observable.of(actions.getChannelError({err})));

        } else if (showAction.payload.parentId) {
          // If the action has a parentId but not channel data, retrieve the channel data and set it on the state
          return watchChannel(action$, showAction.payload.parentId, store.getState(), channel, dependencies)
            .catch(err => Observable.of(actions.getChannelError({err})));

        } else if (showAction.payload.target && showAction.payload.target.id && showAction.payload.target.collection) {
          // if the action has a target, retrieve the channel (crete it if it doesn't exist) and return the channel ID
          // emit an action that will set th channel data and the parentId in the modal state

          return watchChannel(action$, showAction.payload.target.id, store.getState(), channel, dependencies)
            .catch(ajaxError => {
              if (ajaxError.status === 404) {
                return linkChannelFactory.sendRequest(showAction.payload.target, store.getState())
                  .mergeMap(ajaxResponse => Observable.merge(
                    Observable.of(actions.linkChannelSuccess({channel: ajaxResponse.response})),
                    watchPermissions(ajaxResponse.response.org),
                  ))
                  .catch(err => Observable.of(actions.linkChannelError({err})));
              } else {
                return Observable.of(actions.getChannelError({err: ajaxError}));
              }
            });
        } else {
          // log error?

          return Observable.empty();
        }
      })
      .catch(epicErrorHandler(logger, 'onShowModalGetOrLinkChannel'));
  }

  /**
   * Reset the request state after a delay.
   * @param {ActionsObservable<ReduxAction>} action$
   * @param {Store} store
   * @return {Observable<any>}
   */
  function onRequestFinalize(action$, store) {
    return action$.ofType(actions.updateSubscription.requestSuccess.TYPE, actions.updateSubscription.requestError.TYPE)
      .switchMap(() =>
        Observable.timer(750)
          .mapTo(actions.updateSubscription.resetRequest())
          .takeUntil(action$.ofType(
            actions.updateSubscription.sendRequest.TYPE,
            actions.showModal.TYPE,
            actions.hideModal.TYPE
          ))
      )
      .catch(epicErrorHandler(logger, 'onShowModalGetOrLinkChannel'));
  }

  /**
   * @param {ActionsObservable<ReduxAction>} action$
   * @param {Store} store
   * @return {Observable<any>}
   */
  function getUpdatedChannel(action$, store) {
    return action$.ofType(actions.showModal.TYPE)
      .switchMap(showAction =>
        action$.ofType(actions.updateSubscription.requestSuccess.TYPE)
          .switchMap(action => {
            const ajaxResponse = action.payload.ajaxResponse;
            if (ajaxResponse.response) {
              return Observable.of(actions.getChannelSuccess({channel: ajaxResponse.response}));
            } else {
              return getChannel({channelId: showAction.payload.parentId}, store.getState());
            }
          })
          .catch(err => Observable.of(actions.getChannelError({err})))
          .takeUntil(action$.ofType(actions.hideModal.TYPE))
      )
      .catch(epicErrorHandler(logger, 'onShowModalGetOrLinkChannel'));
  }

  /**
   * Watch for updated channel data
   * Watch the org's application-level permissions
   *  - get org ID, then watch permissions and dispatch action to set permissions state for modal
   *
   * @param {ActionsObservable<ReduxAction>} action$
   * @param {String} channelId
   * @param rootState
   * @param {Object} channel
   * @param dependencies
   * @return {Observable<ReduxAction>}
   */
  function watchChannel(action$, channelId, rootState, channel, dependencies) {
    const watchPermissions = buildWatchPermissions(dependencies);
    let update$ = buildInstanceSyncNotifier(CHAT_CHANNEL_COLLECTION, `Failed to read channel ${channelId}`)(channelId);

    if (!channel) {
      update$ = update$.startWith(null);
    }

    update$ = update$
      .mergeMap(() => getChannel({channelId}, rootState));

    let permissions$;
    if (channel) {
      // we already have the org id in the channel so fetch the permissions immediately
      permissions$ = watchPermissions(channel.org);

    } else {
      // share getChannel so we can read it from 2 separate streams
      update$ = update$.share();

      // we need to get the org id from the channel the first time we receive an update
      permissions$ = update$
        .take(1)
        .switchMap(action => watchPermissions(action.payload.channel.org));
    }

    return Observable
      .merge(
        update$,
        permissions$,
      )
      .takeUntil(action$.ofType(actions.showModal.TYPE));
  }

  function getChannel(request, rootState) {
    return getChannelFactory.sendRequest(request, rootState)
      .map(ajaxResponse => actions.getChannelSuccess({channel: ajaxResponse.response}));
  }

  function buildWatchPermissions(dependencies) {
    return function watchPermissions(orgId) {
      return dependencies.applicationPermissionsService.watchPermissions(orgId)
        .map(permissions => actions.getAppPermissionsSuccess({permissions}))
        .catch(err => Observable.of(actions.getAppPermissionsError({err})));
    };
  }
}

function buildGetChannelFactory(options) {
  return new AjaxFactory(buildGetChannelRequestOptions(options));
}

/**
 * @param options
 * @return {AjaxFactoryOptions}
 */
function buildGetChannelRequestOptions(options) {
  return {
    crossDomain: options.crossDomain,
    method: HttpMethod.GET,
    useBody: false,
    url: request => `${options.baseApiUrl}/chat-channels/${request.channelId}`,
    getToken: options.getToken,
  };
}

function buildLinkChannelFactory(options) {
  return new AjaxFactory(buildLinkChannelRequestOptions(options));
}

/**
 * @param options
 * @return {AjaxFactoryOptions}
 */
function buildLinkChannelRequestOptions(options) {
  return {
    crossDomain: options.crossDomain,
    method: HttpMethod.POST,
    useBody: true,
    url: request => `${options.baseApiUrl}/chat-channels/_link`,
    getToken: options.getToken,
  };
}



// WEBPACK FOOTER //
// ./src/js/comments/epics/modalSupportEpics.js